import React from 'react';
import { MdErrorOutline, MdInfoOutline } from 'react-icons/md';

interface AnalysisNoticeBannerProps {
    /** Documentos cuja análise falhou — reprocessável. */
    errorDocuments: string[];
    /** Documentos sem dado no bureau — não reprocessável. */
    noDataDocuments: string[];
    /** AF-4082: documentos interrompidos sem observação — bloqueiam o salvamento do parecer. */
    interruptedDocuments?: string[];
}

const SAMPLE_SIZE = 3;

const formatSample = (documents: string[]): string => {
    const sample = documents.slice(0, SAMPLE_SIZE).join(', ');
    return documents.length > SAMPLE_SIZE ? `${sample}…` : sample;
};

/**
 * AF-3460: avisos sobre documentos que não foram avaliados.
 *
 * Os dois casos aparecem separados de propósito, porque a ação esperada é diferente:
 *  - erro na análise: reprocessar o lote muda o resultado
 *  - sem dados: o bureau respondeu e o documento não tem informação naquela base;
 *    reprocessar devolve o mesmo, então cabe decisão manual do analista
 *
 * Antes os dois eram "erro" e a mensagem mandava recarregar os lotes, o que para o caso
 * "sem dados" era uma instrução inútil.
 *
 * A faixa é fixa (não é toast) para o analista ver a situação enquanto trabalha, antes de
 * decidir o parecer.
 */
const AnalysisNoticeBanner: React.FC<AnalysisNoticeBannerProps> = ({
    errorDocuments,
    noDataDocuments,
    interruptedDocuments = [],
}) => {
    if (
        errorDocuments.length === 0 &&
        noDataDocuments.length === 0 &&
        interruptedDocuments.length === 0
    ) {
        return null;
    }

    return (
        <div className="mb-4 space-y-2">
            {errorDocuments.length > 0 && (
                <div
                    className="flex items-start gap-3 rounded-lg border border-orange-300 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950/40"
                    role="alert"
                >
                    <MdErrorOutline
                        className="mt-0.5 shrink-0 text-orange-600 dark:text-orange-400"
                        size={20}
                        aria-hidden="true"
                    />
                    <div className="min-w-0 text-sm">
                        <p className="font-semibold text-orange-900 dark:text-orange-100">
                            {errorDocuments.length}{' '}
                            {errorDocuments.length === 1 ? 'documento' : 'documentos'} com erro na
                            análise
                        </p>
                        <p className="mt-0.5 text-orange-800 dark:text-orange-200">
                            {formatSample(errorDocuments)}
                        </p>
                        <p className="mt-1 text-orange-700 dark:text-orange-300">
                            Recarregue os lotes para reprocessar. O parecer não pode ser salvo
                            enquanto houver erro.
                        </p>
                    </div>
                </div>
            )}

            {noDataDocuments.length > 0 && (
                <div
                    className="flex items-start gap-3 rounded-lg border border-gray-300 bg-gray-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                    role="status"
                >
                    <MdInfoOutline
                        className="mt-0.5 shrink-0 text-gray-500 dark:text-gray-400"
                        size={20}
                        aria-hidden="true"
                    />
                    <div className="min-w-0 text-sm">
                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                            {noDataDocuments.length}{' '}
                            {noDataDocuments.length === 1 ? 'documento' : 'documentos'} sem dados
                            para análise
                        </p>
                        <p className="mt-0.5 text-gray-700 dark:text-gray-300">
                            {formatSample(noDataDocuments)}
                        </p>
                        <p className="mt-1 text-gray-600 dark:text-gray-400">
                            O provedor respondeu normalmente, mas não há informação para{' '}
                            {noDataDocuments.length === 1 ? 'este documento' : 'estes documentos'}.
                            Reprocessar o lote devolve o mesmo resultado — avalie manualmente. O
                            parecer pode ser salvo.
                        </p>
                    </div>
                </div>
            )}

            {interruptedDocuments.length > 0 && (
                <div
                    className="flex items-start gap-3 rounded-lg border border-slate-300 bg-slate-50 p-3 dark:border-slate-600 dark:bg-slate-800"
                    role="status"
                >
                    <MdInfoOutline
                        className="mt-0.5 shrink-0 text-slate-500 dark:text-slate-400"
                        size={20}
                        aria-hidden="true"
                    />
                    <div className="min-w-0 text-sm">
                        <p className="font-semibold text-slate-900 dark:text-slate-100">
                            {interruptedDocuments.length}{' '}
                            {interruptedDocuments.length === 1 ? 'documento' : 'documentos'} com
                            processamento interrompido
                        </p>
                        <p className="mt-0.5 text-slate-700 dark:text-slate-300">
                            {formatSample(interruptedDocuments)}
                        </p>
                        <p className="mt-1 text-slate-600 dark:text-slate-400">
                            O processamento{' '}
                            {interruptedDocuments.length === 1 ? 'deste item' : 'destes itens'} foi
                            interrompido; o parecer é parcial. Registre uma observação{' '}
                            {interruptedDocuments.length === 1 ? 'no item' : 'em cada item'} (na linha ou
                            pelo botão "Observação em lote") para poder salvar o parecer.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalysisNoticeBanner;
